/**
 * Warehouse Task API Functions
 *
 * API functions for warehouse task management operations
 */

import type {
  WarehouseTask,
  CreateWarehouseTaskDto,
  UpdateWarehouseTaskDto,
  TaskKpis,
  WarehouseTaskStatus,
  WarehouseTaskPriority,
  WarehouseTaskType,
} from '@lognai/plugin-warehouse';
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import type { ListQueryParams } from './client';
import { apiFetch, buildQueryString } from './client';

const BASE_URL = '/api/warehouse';

/**
 * Task filter object for structured filtering
 *
 * Supports:
 * - Direct filters: status, priority, type
 * - Ref filters (simple string searches across multiple fields):
 *   - warehouse: searches name OR code (backend handles multi-field search)
 *   - assignee: searches fullName OR email (backend handles multi-field search)
 *
 * @example
 * // Filter by status and search warehouse
 * { status: 'pending', warehouse: 'NYC' }
 *
 * // Search assignee by name or email
 * { assignee: 'john' }
 */
export interface TaskFilter {
  status?: WarehouseTaskStatus;
  priority?: WarehouseTaskPriority;
  type?: WarehouseTaskType;
  /** Search warehouse by name or code (simple string, backend searches both fields) */
  warehouse?: string;
  /** Search assignee by name or email (simple string, backend searches both fields) */
  assignee?: string;
}

/**
 * Task query parameters: pagination + nested filter
 */
export type TaskFilterParams = ListQueryParams<TaskFilter>;

/**
 * Paginated task list response
 */
export interface PaginatedTaskList {
  items: WarehouseTask[];
  pagination: PaginationMetadata | null;
}

/**
 * Get tasks for a warehouse
 */
export const getTasksByWarehouse = async (
  warehouseId: string,
  params?: TaskFilterParams
): Promise<PaginatedTaskList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedTaskList>(`${BASE_URL}/${warehouseId}/tasks${query}`);
};

/**
 * Get tasks assigned to a user
 */
export const getTasksByAssignee = async (
  userId: string,
  params?: TaskFilterParams
): Promise<PaginatedTaskList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedTaskList>(`${BASE_URL}/users/${userId}/tasks${query}`);
};

/**
 * Get a single task by ID
 */
export const getTask = async (id: string): Promise<WarehouseTask> => {
  return apiFetch<WarehouseTask>(`${BASE_URL}/tasks/${id}`);
};

/**
 * Create a new warehouse task
 */
export const createTask = async (dto: CreateWarehouseTaskDto): Promise<WarehouseTask> => {
  return apiFetch<WarehouseTask>(`${BASE_URL}/tasks`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
};

/**
 * Update an existing warehouse task
 */
export const updateTask = async (
  id: string,
  dto: UpdateWarehouseTaskDto
): Promise<WarehouseTask> => {
  return apiFetch<WarehouseTask>(`${BASE_URL}/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
};

/**
 * Delete a warehouse task
 */
export const deleteTask = async (id: string): Promise<void> => {
  return apiFetch<void>(`${BASE_URL}/tasks/${id}`, {
    method: 'DELETE',
  });
};

/**
 * Get task KPIs for a warehouse
 */
export const getTaskKpis = async (warehouseId: string): Promise<TaskKpis> => {
  return apiFetch<TaskKpis>(`${BASE_URL}/${warehouseId}/tasks/kpis`);
};

/**
 * Get all tasks across all warehouses
 */
export const getAllTasks = async (
  params?: TaskFilterParams
): Promise<PaginatedTaskList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedTaskList>(`${BASE_URL}/tasks${query}`);
};

/**
 * Get aggregated task KPIs across all warehouses
 */
export const getAllTaskKpis = async (): Promise<TaskKpis> => {
  return apiFetch<TaskKpis>(`${BASE_URL}/tasks/kpis`);
};

/**
 * Get task KPIs for tasks assigned to user
 */
export const getTasksByAssigneeKpis = async (userId: string): Promise<TaskKpis> => {
  return apiFetch<TaskKpis>(`${BASE_URL}/users/${userId}/tasks/kpis`);
};
